import type { CareerJobResponse } from "./types/career-job-response";
import type { JobDetailsData } from "./types/job-details-data";

import { mapJobToJobDetailsData } from "./map-job-to-job-details-data";
import { marketingApiGet } from "./marketing-api-get";

const DIGITS_ONLY_PATTERN = /^\d+$/;

type GetJobByIdOptions = {
  franchiseOfficeId?: string | number;
};

function buildJobByIdPath(
  jobId: number,
  franchiseOfficeId?: string | number,
): string {
  const path = `/api/v1/jobs/${jobId}`;

  if (franchiseOfficeId === undefined) {
    return path;
  }

  const searchParams = new URLSearchParams({
    franchiseOfficeId: String(franchiseOfficeId),
  });

  return `${path}?${searchParams.toString()}`;
}

function parseJobId(jobId: string): number | null {
  if (!DIGITS_ONLY_PATTERN.test(jobId)) {
    return null;
  }

  const parsed = Number(jobId);
  if (!Number.isInteger(parsed) || parsed < 1) {
    return null;
  }
  return parsed;
}

async function getJobById(
  jobId: string,
  options: GetJobByIdOptions = {},
): Promise<JobDetailsData | null> {
  const parsedJobId = parseJobId(jobId);

  if (parsedJobId === null) {
    return null;
  }

  const response = await marketingApiGet<CareerJobResponse | null>({
    emptyValue: null,
    logContext: {
      franchiseOfficeId: options.franchiseOfficeId,
      jobId: parsedJobId,
    },
    logMessage: "Failed to fetch job details from hi-marketing-api",
    logTags: { component: "getJobById" },
    path: buildJobByIdPath(parsedJobId, options.franchiseOfficeId),
  });

  if (response === null) {
    return null;
  }

  return mapJobToJobDetailsData(response);
}

export { getJobById };
